import React, { useCallback, useState } from 'react';
import useSWR from 'swr';
import { useAuth } from '../hooks/useAuth';
import { UnreadContext } from '../hooks/useUnread';
import { swrCacheProvider, apiRequest } from '../lib/api';

export function UnreadProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  // Fetcher always hits the API
  const fetcher = async (): Promise<{ count: number }> => {
    const data = await apiRequest<{ count: number }>('/api/summaries/unread-count');
    await swrCacheProvider.set('/api/summaries/unread-count', data);
    return data;
  };

  const { data: unreadData, mutate } = useSWR<{ count: number }>(
    user ? '/api/summaries/unread-count' : null,
    fetcher,
    { revalidateOnFocus: true, revalidateOnReconnect: true, refreshWhenHidden: false }
  );

  // Derive count directly from SWR data; 0 when signed out
  const unreadSummaryCount = user ? (unreadData?.count ?? 0) : 0;

  const [unreadNoticeMinistries, setUnreadNoticeMinistries] = useState<Set<string>>(new Set());

  // When signed out, expose empty set without mutating state
  const effectiveMinistries = user ? unreadNoticeMinistries : new Set<string>();

  const markSummaryRead = useCallback(() => {
    // Kept for API compatibility
  }, []);

  // Optimistically decrement count in SWR cache
  const decrementUnreadCount = useCallback(() => {
    mutate(
      (current) => current ? { count: Math.max(0, current.count - 1) } : current,
      false
    );
  }, [mutate]);

  const reportMinistryUnreads = useCallback((ministry: string, count: number) => {
    setUnreadNoticeMinistries(prev => {
      const newSet = new Set(prev);
      if (count > 0) {
        newSet.add(ministry);
      } else {
        newSet.delete(ministry);
      }
      return newSet;
    });
  }, []);

  return (
    <UnreadContext.Provider value={{ unreadSummaryCount, unreadNoticeMinistries: effectiveMinistries, markSummaryRead, reportMinistryUnreads, decrementUnreadCount }}>
      {children}
    </UnreadContext.Provider>
  );
}